import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { searchCep } from "../../../extras";
import {
  validateArray,
  validateOnlyNumbersIdentification,
} from "../../../common/validators";

// HELPERS
import { DOCUMENT_HELPER } from "../../../common/helpersTxt";
// FRAMES
import {
  fiscalTicketTypeFrame,
  stateInscriptionIndicatorFrame,
} from "../../../common/frames";
// APP COMPONENTS
import AddressForm from "../../address/AddressForm";
// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Popper from "@mui/material/Popper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import { LightDarkGreyBox } from "../../styled";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import AlertModal from "../../modals/alerts/alertModal";
import Stack from "@mui/material/Stack";
import { ButtonDarkGrey, ButtonInfoNoShadow } from "../../styled/button";
import { getDeliveryCenterConfig } from "../../../api/DeliveryCenter/deliveryCenter";
import { formatFinance } from "../../../utils/formatters";

// Custom Popper component with proper z-index for modal usage
const ModalPopper = (props) => {
  return (
    <Popper
      {...props}
      style={{
        ...props.style,
        zIndex: 10001, // Higher than modal z-index (10000)
      }}
      placement="bottom-start"
      modifiers={[
        {
          name: "preventOverflow",
          enabled: true,
          options: {
            boundary: "viewport",
          },
        },
        {
          name: "flip",
          enabled: true,
        },
      ]}
    />
  );
};

export default function NewFiscalForm({
  index,
  value,
  identification,
  customerName,
  orderStatusNumber,
  submit,
}) {
  const [fiscalTicketType, setFiscalTicketType] = useState(
    fiscalTicketTypeFrame[0]
  );
  const [fiscalIdentification, setFiscalIdentification] = useState("");

  const [showAddressModal, setShowAddressModal] = useState(false);

  const [addressId, setAddressId] = useState(uuidv4());
  const [cep, setCep] = useState(null);
  const [street, setStreet] = useState(null);
  const [houseNumber, setHouseNumber] = useState(null);
  const [neighborhood, setNeighborhood] = useState(null);
  const [complement, setComplement] = useState(null);
  const [reference, setReference] = useState(null);
  const [city, setCity] = useState(null);
  const [uf, setUf] = useState(null);

  const [name, setName] = useState(customerName);
  const [stateInscriptionIndicator, setStateInscriptionIndicator] = useState(
    stateInscriptionIndicatorFrame[0]
  );
  const [stateInscription, setStateInscription] = useState(null);

  const [allowDefaultFiscalItem, setAllowDefaultFiscalItem] = useState(false);

  const [alertModalMessage, setAlertModalMessage] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    setFiscalIdentification(applyMask(identification));

    const loadConfig = async () => {
      try {
        const params = {
          config: "allow_default_fiscal_infos",
        };
        const {
          data: { allow_default_fiscal_infos },
        } = await getDeliveryCenterConfig(params);
        setAllowDefaultFiscalItem(allow_default_fiscal_infos);
      } catch (err) {
        console.error(err);
      }
    };

    loadConfig();
  }, []);

  useEffect(() => {
    if (fiscalTicketType.value === 3) {
      handleShowAddressForm();
    } else {
      setShowAddressModal(false);
    }
  }, [fiscalTicketType.value]);

  const _searchCep = async () => {
    if (cep && cep.length === 8) {
      const data = await searchCep(cep);

      setStreet(data.logradouro);
      setNeighborhood(data.bairro);
      setCity(data.localidade);
      setUf(data.uf);
    }
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    const formattedValue = applyMask(value);
    setFiscalIdentification(formattedValue);

    const isValid = validateOnlyNumbersIdentification(value);
    if (!isValid) {
      setError(true);
    } else {
      setError(false);
    }
  };

  const applyMask = (value) => {
    const onlyNumbers = value.replace(/\D/g, "");
    if (onlyNumbers.length <= 11) {
      return onlyNumbers
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})/, "$1-$2")
        .replace(/(-\d{2})\d+?$/, "$1");
    } else {
      return onlyNumbers
        .replace(/(\d{2})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1/$2")
        .replace(/(\d{4})(\d{1,2})/, "$1-$2")
        .replace(/(-\d{2})\d+?$/, "$1");
    }
  };

  const handleShowAddressForm = () => {
    setShowAddressModal(!showAddressModal);
  };

  const handleShowAlertModal = () => {
    setShowAlertModal(!showAlertModal);
  };

  const handleSubmit = (e, isGeneral = false) => {
    if (fiscalTicketType.value === 3) {
      const isAddressValid = validateData();
      if (!isAddressValid) {
        handleAlertModalData(
          "Dados Incompletos: Os de endereço e nome são obrigatórios para a emissão de NF-e"
        );
        return;
      }

      if (stateInscriptionIndicator.value === 1) {
        const isStateInscriptionValid = validateArray([stateInscription]);
        if (!isStateInscriptionValid) {
          handleAlertModalData(
            "Dados Incompletos: O campo Inscrição Estadual é necessário"
          );
          return;
        }
      }
    }

    const extraInfoObj = {
      cep,
      street,
      houseNumber,
      neighborhood,
      complement,
      city,
      uf,
      stateInscription,
      name,
      stateInscriptionIndicator: stateInscriptionIndicator.value,
      isGeneral,
    };

    submit(fiscalTicketType.value, fiscalIdentification, extraInfoObj);
  };

  const validateData = () => {
    const valuesToValidate = [
      cep,
      street,
      houseNumber,
      neighborhood,
      city,
      uf,
      name,
    ];
    return validateArray(valuesToValidate);
  };

  const handleAlertModalData = (message) => {
    setAlertModalMessage(message);
    setShowAlertModal(true);
  };

  return (
    <Stack
      spacing={0}
      display={"flex"}
      justifyContent={"center"}
      height={"auto"}
    >
      <Stack spacing={2} sx={{ width: "70%", margin: "auto", p: 4 }}>
        <LightDarkGreyBox>
          <Typography align="center" variant="h4">
            Pedido {index}
          </Typography>
        </LightDarkGreyBox>

        <LightDarkGreyBox>
          <Typography align="center" variant="h4">
            {formatFinance(value ?? 0)}
          </Typography>
        </LightDarkGreyBox>

        {orderStatusNumber < 34 && (
          <Alert variant="filled" severity="warning" sx={{ my: 2 }}>
            <Typography align="center" variant="body1">
              ATENÇÃO
            </Typography>
            <Typography align="center" variant="body2">
              O pedido será finalizado e não poderá ser editado após a emissão
              do Cupom Fiscal
            </Typography>
          </Alert>
        )}

        <Autocomplete
          fullWidth
          value={fiscalTicketType}
          onChange={(event, newValue) => {
            setFiscalTicketType(newValue);
          }}
          options={fiscalTicketTypeFrame}
          getOptionLabel={(option) => option.label}
          renderInput={(params) => (
            <TextField {...params} label="Tipo do Cupom" variant="outlined" />
          )}
          PopperComponent={ModalPopper}
          sx={{ mt: 4, mb: 1 }}
          disablePortal={false} // Allow portal to render in document.body
        />

        <TextField
          id="cpf-cnpj-input"
          label="CPF/CNPJ"
          variant="outlined"
          value={fiscalIdentification}
          onChange={handleInputChange}
          error={error}
          helperText={error ? DOCUMENT_HELPER : ""}
          fullWidth
        />

        {showAddressModal && (
          <>
            <Autocomplete
              size="small"
              fullWidth
              value={stateInscriptionIndicator}
              onChange={(event, newValue) => {
                setStateInscriptionIndicator(newValue);
              }}
              options={stateInscriptionIndicatorFrame}
              getOptionLabel={(option) => option.label}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Regime Tributário"
                  variant="outlined"
                />
              )}
              PopperComponent={ModalPopper}
              disablePortal={false}
            />

            <TextField
              id="customer-name-nfe"
              label="Nome para Nota Fiscal"
              variant={"outlined"}
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
            />

            <TextField
              id="state-inscription-nfe"
              label="Inscrição Estadual"
              variant={"outlined"}
              fullWidth
              autoComplete={"state-inscription-nfe"}
              value={stateInscription}
              onChange={(e) => setStateInscription(e.target.value)}
            />
          </>
        )}
      </Stack>

      {showAddressModal && (
        <>
          <Divider sx={{ my: 2 }}>
            <Chip label="Endereço para Nota Fiscal" />
          </Divider>
          <AddressForm
            addressId={addressId}
            setAddressId={setAddressId}
            cep={cep}
            setCep={setCep}
            street={street}
            setStreet={setStreet}
            houseNumber={houseNumber}
            setHouseNumber={setHouseNumber}
            neighborhood={neighborhood}
            setNeighborhood={setNeighborhood}
            complement={complement}
            setComplement={setComplement}
            reference={reference}
            setReference={setReference}
            city={city}
            setCity={setCity}
            uf={uf}
            setUf={setUf}
            searchCep={_searchCep}
            showDataWarning={false}
          />
        </>
      )}

      <Box
        sx={{
          m: "auto",
          my: 2,
        }}
      >
        <ButtonDarkGrey
          variant="contained"
          color="success"
          size="large"
          onClick={(e) => handleSubmit(e)}
        >
          Emitir {fiscalTicketType.value === 2 ? "Cupom Fiscal" : "Nota Fiscal"}{" "}
          Detalhado
        </ButtonDarkGrey>
        {allowDefaultFiscalItem && (
          <ButtonInfoNoShadow
            variant="contained"
            color="success"
            size="large"
            onClick={(e) => handleSubmit(e, true)}
            sx={{ ml: 2 }}
          >
            Emitir{" "}
            {fiscalTicketType.value === 2 ? "Cupom Fiscal" : "Nota Fiscal"}{" "}
            Geral
          </ButtonInfoNoShadow>
        )}
      </Box>

      {showAlertModal && (
        <AlertModal
          show={showAlertModal}
          btnLabel={"OK"}
          message={alertModalMessage}
          action={handleShowAlertModal}
          // Ensure alert modal appears above fiscal modal
          style={{ zIndex: 10002 }}
        />
      )}
    </Stack>
  );
}
